import { useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import {
  ApiError,
  api,
  refreshSession,
  setAccessToken,
  setSessionLostHandler,
  unwrap,
  type AuthResponse,
  type Me,
  type User,
} from "@/api/client";
import i18n from "@/i18n";

type AuthState =
  { status: "loading"; me: null } | { status: "anonymous"; me: null } | { status: "authenticated"; me: Me };

/** Result of the password step: either signed in or a second factor is needed. */
export type LoginResult = { mfaToken: string } | null;

type AuthContextValue = AuthState & {
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (mfaToken: string, code: string) => Promise<void>;
  /** Starts a session from an auth response (invitation, first-run setup). */
  signIn: (res: AuthResponse) => void;
  /** Replaces the cached profile after it changed. */
  setMe: (me: Me) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = useState<AuthState>({ status: "loading", me: null });

  const signedOut = useCallback(() => {
    setAccessToken(null);
    qc.clear();
    setState({ status: "anonymous", me: null });
  }, [qc]);

  const signIn = useCallback((res: AuthResponse) => {
    setAccessToken(res.access_token);
    setState({ status: "authenticated", me: res.me });
    // The interface follows the language saved in the profile.
    if (i18n.resolvedLanguage !== res.me.user.locale) void i18n.changeLanguage(res.me.user.locale);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSessionLostHandler(signedOut);
    // Restore the session from the refresh cookie.
    void refreshSession().then((s) => {
      if (cancelled) return;
      if (s) signIn(s);
      else setState({ status: "anonymous", me: null });
    });
    return () => {
      cancelled = true;
      setSessionLostHandler(null);
    };
  }, [signedOut, signIn]);

  const login = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const res = await api.POST("/api/v1/auth/login", { body: { email, password } });
      const data = unwrap(res);
      if ("mfa_token" in data) return { mfaToken: data.mfa_token };
      signIn(data);
      return null;
    },
    [signIn],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const res = await api.POST("/api/v1/auth/mfa/verify", { body: { mfa_token: mfaToken, code } });
      signIn(unwrap(res));
    },
    [signIn],
  );

  const setMe = useCallback((me: Me) => {
    setState((s) => (s.status === "authenticated" ? { status: "authenticated", me } : s));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.POST("/api/v1/auth/logout", {});
    } catch (e) {
      if (!(e instanceof ApiError)) console.error(e);
    }
    signedOut();
  }, [signedOut]);

  const value = useMemo(
    () => ({ ...state, login, verifyMfa, signIn, setMe, logout }),
    [state, login, verifyMfa, signIn, setMe, logout],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/** The signed-in user with permissions; only for pages behind RequireAuth. */
// eslint-disable-next-line react-refresh/only-export-components
export function useMe(): Me {
  const auth = useAuth();
  if (auth.status !== "authenticated") throw new Error("useMe outside an authenticated route");
  return auth.me;
}

/** The signed-in user; only for pages behind RequireAuth. */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrentUser(): User {
  return useMe().user;
}

// eslint-disable-next-line react-refresh/only-export-components
export const isStaff = (u: User) => u.role === "admin" || u.role === "manager";
